import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useService } from '@shared/hooks/use-services';
import { formatRupees } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { StartTransactionDialog } from './start-transaction-dialog';

export interface ServiceDetailPageProps {
  serviceId?: string;
  onBack?: () => void;
  homePath?: string;
  servicesPath?: string;
}

export function ServiceDetailPage({
  serviceId,
  onBack,
  homePath,
  servicesPath,
}: ServiceDetailPageProps = {}) {
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  const id = serviceId ?? paramId;
  const { data: service, isLoading, isError } = useService(id);
  const [open, setOpen] = useState(false);
  const [showOverview, setShowOverview] = useState(false);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(servicesPath ?? '/client/services');
    }
  };

  if (isLoading) return <p className="p-8 text-sm text-gray-500">Loading…</p>;
  if (isError || !service) {
    return (
      <div className="w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors shadow-xs shrink-0 active:scale-95 cursor-pointer"
            aria-label="Back"
            title="Back"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold">Service not found</h1>
        </div>
        <Link to="/client/services" className="mt-4 inline-block text-sm text-brand-700 underline">
          Back to all services
        </Link>
      </div>
    );
  }

  // Calculate detailed fee breakdown & totals
  const assocFeeNum = Number(
    service.defaultAssociateFeeRupees ?? service.defaultApplicationFeeRupees ?? 0,
  );
  const platformFeeNum = Number(
    service.defaultPlatformFeeRupees ?? service.defaultServiceChargeRupees ?? 0,
  );

  const assocGstNum = Number(service.associateGstPercentage ?? service.gstPercentage ?? 18);
  const platformGstNum = Number(service.platformGstPercentage ?? service.gstPercentage ?? 18);

  const assocGstAmt = Number(service.associateGstAmountRupees) || assocFeeNum * (assocGstNum / 100);
  const platformGstAmt =
    Number(service.platformGstAmountRupees) || platformFeeNum * (platformGstNum / 100);

  const totalAssocFee = Number(service.totalAssociateFeeRupees) || assocFeeNum + assocGstAmt;
  const totalPlatformFee =
    Number(service.totalPlatformFeeRupees) || platformFeeNum + platformGstAmt;

  const additionalCharges = Array.isArray(service.additionalCharges)
    ? service.additionalCharges
    : [];

  let addlSubtotal = 0;
  let addlGstTotal = 0;
  const computedAddlCharges = additionalCharges.map((chg, idx) => {
    const amt = Number(chg.amountRupees || 0);
    const gstPct = Number(chg.gstPercentage ?? 18);
    const gstAmt = Number(chg.gstAmountRupees) || amt * (gstPct / 100);
    const totalAmt = Number(chg.totalAmountRupees) || amt + gstAmt;
    addlSubtotal += amt;
    addlGstTotal += gstAmt;
    return {
      id: chg.id || String(idx),
      name: chg.name || `Additional Charge #${idx + 1}`,
      amountRupees: amt,
      gstPercentage: gstPct,
      gstAmountRupees: gstAmt,
      totalAmountRupees: totalAmt,
    };
  });

  const govtFeeNum = Number(service.governmentFeeRupees ?? 0);

  const subtotalWithoutGst = assocFeeNum + platformFeeNum + addlSubtotal + govtFeeNum;
  const gstTotal = assocGstAmt + platformGstAmt + addlGstTotal;
  const grandTotal = Number(service.grandTotalRupees) || subtotalWithoutGst + gstTotal;

  return (
    <div className="w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      {/* Header & Navigation */}
      <div className="flex items-start gap-3.5 mb-6">
        <button
          type="button"
          onClick={handleBack}
          className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-brand-50 hover:text-brand-800 hover:border-brand-300 transition-all shadow-xs shrink-0 active:scale-95 cursor-pointer"
          aria-label="Back"
          title="Back"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <nav className="truncate text-xs text-gray-500 sm:text-sm">
            <Link to={homePath ?? '/client/home'} className="hover:underline">
              {homePath ? 'Transactions' : 'Home'}
            </Link>
            {' / '}
            <Link
              to={servicesPath ?? '/client/services'}
              onClick={(e) => {
                if (onBack) {
                  e.preventDefault();
                  onBack();
                }
              }}
              className="hover:underline"
            >
              {servicesPath ? 'Take a Service' : 'All Services'}
            </Link>
            {' / '}
            <span className="text-gray-900 font-medium">{service.name}</span>
          </nav>
          <h1 className="mt-1 text-2xl font-bold text-gray-900 sm:text-3xl">{service.name}</h1>
        </div>
      </div>

      {/* Responsive Grid: Details on Left (2 cols), Sticky Checkout/Pricing on Right (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Service Image Card */}
          {service.images && service.images[0] ? (
            <div className="w-full rounded-2xl border border-gray-200/80 bg-gradient-to-b from-slate-50 to-gray-100/60 p-4 sm:p-6 flex items-center justify-center min-h-[220px] sm:min-h-[280px] shadow-xs">
              <img
                src={service.images[0]}
                alt={service.name}
                className="max-h-60 sm:max-h-72 w-auto max-w-full object-contain rounded-lg drop-shadow-sm"
              />
            </div>
          ) : null}

          {/* Description */}
          {service.description ? (
            <div className="rounded-2xl border border-gray-200/80 bg-white p-5 sm:p-6 shadow-xs">
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                About This Service
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {service.description}
              </p>
            </div>
          ) : null}

          {/* Required Documents */}
          {service.requiredDocuments && service.requiredDocuments.length > 0 ? (
            <section className="rounded-2xl border border-gray-200/80 bg-white p-5 sm:p-6 shadow-xs">
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3.5">
                Required Documents
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {service.requiredDocuments.map((doc) => (
                  <div
                    key={doc}
                    className="flex items-center gap-2.5 rounded-xl border border-gray-100 bg-gray-50/70 px-3.5 py-2.5 text-sm font-medium text-gray-800"
                  >
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    </div>
                    <span>{doc}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* Sub Services */}
          {service.subServices && service.subServices.length > 0 ? (
            <section className="rounded-2xl border border-gray-200/80 bg-white p-5 sm:p-6 shadow-xs">
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3.5">
                Sub-services Included
              </h2>
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-200/80 overflow-hidden">
                {service.subServices.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between p-3.5 bg-white hover:bg-gray-50/60 transition-colors"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{s.name}</div>
                      {s.subCategory ? (
                        <div className="text-xs text-gray-500 mt-0.5">{s.subCategory}</div>
                      ) : null}
                    </div>
                    {s.defaultServiceChargeRupees ? (
                      <div className="text-sm font-bold text-gray-800">
                        {formatRupees(s.defaultServiceChargeRupees)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>

        {/* Right Sticky Pricing Sidebar */}
        <div className="lg:col-span-1 lg:sticky lg:top-6">
          <div className="rounded-2xl border border-brand-200 bg-gradient-to-b from-brand-50/70 via-white to-white p-5 sm:p-6 shadow-sm space-y-5">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-800">
                  {service.isStartsFrom ? 'Starting From' : 'Total Service Fee'}
                </span>
                <button
                  type="button"
                  onClick={() => setShowOverview(true)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:text-brand-900 bg-white hover:bg-brand-100/70 border border-brand-200 px-2.5 py-1 rounded-lg transition-all shadow-2xs active:scale-95 cursor-pointer"
                >
                  <span>Overview</span>
                  <svg
                    className="w-3 h-3 text-brand-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
              </div>

              {service.isStartsFrom ? (
                <div className="mt-2 text-2xl font-extrabold text-brand-900 sm:text-3xl">
                  Starts from {formatRupees(service.startsFromRupees ?? 0)}
                </div>
              ) : (
                <div className="mt-2 text-3xl font-extrabold text-brand-900 sm:text-4xl tracking-tight">
                  {formatRupees(grandTotal)}
                </div>
              )}

              {service.timelineText ? (
                <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 border border-emerald-200/60">
                  <span>⏱ Timeline: {service.timelineText}</span>
                </div>
              ) : null}
            </div>

            <hr className="border-brand-100" />

            <Button
              block
              className="w-full text-base py-3 shadow-sm hover:shadow transition-all"
              onClick={() => setOpen(true)}
            >
              Start this service
            </Button>

            <p className="text-xs text-gray-500 text-center leading-relaxed">
              Starting a service opens a chat thread with an Associate. An invoice will follow — you
              can pay directly from the chat.
            </p>
          </div>
        </div>
      </div>

      <StartTransactionDialog open={open} onClose={() => setOpen(false)} service={service} />

      {/* Fee Breakdown Overview Modal */}
      {showOverview ? (
        <Dialog
          open
          onClose={() => setShowOverview(false)}
          title="Fee Breakdown Overview"
          className="max-w-xl sm:max-w-xl"
          footer={<Button onClick={() => setShowOverview(false)}>Close</Button>}
        >
          {service.isStartsFrom ? (
            <div className="space-y-4 text-sm text-gray-700">
              <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-4">
                <p className="mt-1 text-xs text-brand-700 leading-relaxed">
                  This service starts from{' '}
                  <strong className="font-bold">
                    {formatRupees(service.startsFromRupees ?? 0)}
                  </strong>
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <p className="text-xs text-gray-600">
                Detailed breakdown of Associate Fee, Platform Fee, Additional Charges, GST amounts,
                and overall total:
              </p>

              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full min-w-[480px] text-xs text-left">
                  <thead className="bg-gray-50 font-semibold text-gray-700 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2.5 min-w-[130px]">Fee Type</th>
                      <th className="px-3 py-2.5 text-right whitespace-nowrap">Fee (₹)</th>
                      <th className="px-3 py-2.5 text-right whitespace-nowrap">GST %</th>
                      <th className="px-3 py-2.5 text-right whitespace-nowrap">GST Amt (₹)</th>
                      <th className="px-3 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">
                        Total (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-emerald-50/30">
                      <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                        Associate Fee
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{assocFeeNum.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{assocGstNum}%</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{assocGstAmt.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-emerald-800 whitespace-nowrap">
                        ₹{totalAssocFee.toFixed(2)}
                      </td>
                    </tr>

                    <tr className="hover:bg-blue-50/30">
                      <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                        Platform Fee
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{platformFeeNum.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {platformGstNum}%
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{platformGstAmt.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-blue-800 whitespace-nowrap">
                        ₹{totalPlatformFee.toFixed(2)}
                      </td>
                    </tr>

                    <tr className="hover:bg-amber-50/30">
                      <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                        Government Fee
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{govtFeeNum.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">—</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">₹0.00</td>
                      <td className="px-3 py-2.5 text-right font-bold text-amber-800 whitespace-nowrap">
                        ₹{govtFeeNum.toFixed(2)}
                      </td>
                    </tr>

                    {computedAddlCharges.length > 0
                      ? computedAddlCharges.map((chg, idx) => (
                          <tr key={chg.id || idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-900 min-w-[130px] max-w-[170px]">
                              <div className="whitespace-nowrap">Additional Charge</div>
                              {chg.name ? (
                                <div className="text-[10px] font-normal text-gray-500 break-words leading-tight">
                                  ({chg.name})
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              ₹{chg.amountRupees.toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {chg.gstPercentage}%
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              ₹{chg.gstAmountRupees.toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-brand-800 whitespace-nowrap">
                              ₹{chg.totalAmountRupees.toFixed(2)}
                            </td>
                          </tr>
                        ))
                      : null}
                  </tbody>
                  <tfoot className="bg-gray-50/90 font-semibold text-gray-900 border-t-2 border-gray-200">
                    <tr>
                      <td className="px-3 py-2.5 font-bold whitespace-nowrap">Overall Total</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{subtotalWithoutGst.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">—</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        ₹{gstTotal.toFixed(2)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold text-brand-800 whitespace-nowrap">
                        ₹{grandTotal.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}
